/**
 * Typed Action Registry — the SOLE dispatch for view-triggered behavior.
 *
 * Feature modules self-register their actions; the raft SSR views reference
 * them via `data-action`/`data-fn`/`data-on-*` attributes resolved by
 * actionDelegator, and `data-init-fn` resolved by pageInit. Nothing is copied
 * onto `window` — the only globals left are the jQuery compat shim
 * (plugins/globals.ts) and genuine remote-script libraries (amCharts, hcaptcha);
 * server config travels as the non-executable `#__config` JSON data island
 * read by src/config.ts.
 *
 * The cross-app contract (which names views may emit) lives in
 * `@brave-new-world/shared` viewContract (VIEW_ACTIONS / MODAL_KEYS).
 */

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyFunction = (...args: any[]) => any;

const registry = new Map<string, AnyFunction>();

/**
 * Register a single action by name.
 */
export function registerAction(name: string, handler: AnyFunction): void {
  registry.set(name, handler);
}

/**
 * Unregister an action (used by Preact island cleanup).
 */
export function unregisterAction(name: string): void {
  registry.delete(name);
}

/**
 * Register multiple actions at once.
 */
export function registerActions(actions: Record<string, AnyFunction>): void {
  for (const [name, handler] of Object.entries(actions)) {
    registry.set(name, handler);
  }
}

/**
 * Look up a registered action by name.
 * Used by actionDelegator for data-fn dispatch.
 */
export function getAction(name: string): AnyFunction | undefined {
  return registry.get(name);
}
